import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PickerItem {
  id: string;
  label: string;
  sub?: string;
}

interface PickerProps {
  value: { id: string; label: string } | null;
  onChange: (item: { id: string; label: string } | null) => void;
  search: (q: string) => Promise<PickerItem[]>;
  /** Called when user picks "Create new <typed text>". Should return the
   *  freshly-created item so the picker can select it. */
  onCreateNew?: (typedText: string) => Promise<PickerItem | null>;
  placeholder?: string;
  disabled?: boolean;
  /** When set, the dropdown filters by this — used by TripPicker to filter by client. */
  refetchKey?: unknown;
}

/**
 * Typeahead picker with "create new" affordance. Search debounced 200ms.
 * Click outside closes. Esc clears.
 */
export function Picker({
  value,
  onChange,
  search,
  onCreateNew,
  placeholder = 'Search…',
  disabled,
  refetchKey,
}: PickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value?.label ?? '');
  const [items, setItems] = useState<PickerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // sync query with external value (when cleared programmatically)
  useEffect(() => {
    if (!value) setQuery('');
    else setQuery(value.label);
  }, [value?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      setSearchError(false);
      try {
        const r = await search(query);
        setItems(r);
      } catch {
        setItems([]);
        setSearchError(true);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, open, refetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const showCreate =
    open && !!onCreateNew && query.trim().length > 0 &&
    !items.some((i) => i.label.toLowerCase() === query.trim().toLowerCase());

  return (
    <div ref={wrapRef} className="relative">
      <div
        className={cn(
          'flex h-10 w-full items-center rounded-md border border-input bg-background px-3 text-sm',
          disabled && 'opacity-50',
        )}
      >
        <input
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
            if (value && e.target.value !== value.label) onChange(null);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); }
          }}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
        {value ? (
          <button
            type="button"
            onClick={() => { onChange(null); setQuery(''); }}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </div>

      {open ? (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-y-auto rounded-md border bg-popover bg-background p-1 shadow-md">
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </div>
          ) : searchError ? (
            <div className="px-3 py-2 text-xs text-destructive">Search failed — try again</div>
          ) : items.length === 0 && !showCreate ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>
          ) : null}
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                onChange({ id: item.id, label: item.label });
                setQuery(item.label);
                setOpen(false);
              }}
              className={cn(
                'flex w-full flex-col items-start rounded-sm px-3 py-1.5 text-left text-sm hover:bg-accent',
                value?.id === item.id && 'bg-accent',
              )}
            >
              <span>{item.label}</span>
              {item.sub ? <span className="text-[11px] text-muted-foreground">{item.sub}</span> : null}
            </button>
          ))}
          {showCreate ? (
            <button
              type="button"
              disabled={creating}
              onClick={async () => {
                if (!onCreateNew) return;
                setCreating(true);
                try {
                  const created = await onCreateNew(query.trim());
                  if (created) {
                    onChange({ id: created.id, label: created.label });
                    setQuery(created.label);
                    setOpen(false);
                  }
                } finally {
                  setCreating(false);
                }
              }}
              className="flex w-full items-center gap-1.5 rounded-sm border-t px-3 py-2 text-left text-xs text-primary hover:bg-accent"
            >
              {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Create &quot;{query.trim()}&quot;
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
